/**
 * The dope sheet's right-click menu: delete / copy the key selection, retime it (reverse or
 * time-scale) and set its easing.
 * Plain DOM, positioned with `position: fixed` at the pointer, dismissed on Escape, on the next
 * pointer press outside it, or as soon as an item runs.
 */

import { SCALE_FACTOR_MAX, SCALE_FACTOR_MIN, TWEEN_PRESETS } from '../../shared';
import type { TweenPreset } from '../../shared';

/** The one-click time-scale presets; anything else goes through the custom field. */
const SCALE_PRESETS = [0.5, 2];

export interface KeyMenuOptions {
  x: number;
  y: number;
  /** Node the menu is appended to (the timeline root, so disposal removes it too). */
  host: HTMLElement;
  onDelete(): void;
  onCopy(): void;
  onReverse(): void;
  onScale(factor: number): void;
  onEasing(preset: TweenPreset): void;
}

export interface OpenMenu {
  element: HTMLElement;
  close(): void;
}

function button(label: string, className: string): HTMLButtonElement {
  const el = document.createElement('button');
  el.type = 'button';
  el.className = className;
  el.textContent = label;
  return el;
}

export function openKeyContextMenu(options: KeyMenuOptions): OpenMenu {
  const menu = document.createElement('div');
  menu.className = 'tl-menu';
  menu.style.left = `${options.x}px`;
  menu.style.top = `${options.y}px`;

  let closed = false;
  const close = (): void => {
    if (closed) return;
    closed = true;
    document.removeEventListener('pointerdown', onOutside, true);
    document.removeEventListener('keydown', onKeyDown, true);
    menu.remove();
  };

  const onOutside = (event: Event): void => {
    if (event.target instanceof Node && menu.contains(event.target)) return;
    close();
  };
  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
    }
  };

  const run = (fn: () => void) => (): void => {
    close();
    fn();
  };

  const del = button('Delete', 'tl-menu-item');
  del.dataset.tl = 'menu-delete';
  del.addEventListener('click', run(options.onDelete));

  const copy = button('Copy', 'tl-menu-item');
  copy.dataset.tl = 'menu-copy';
  copy.addEventListener('click', run(options.onCopy));

  const reverse = button('Reverse', 'tl-menu-item');
  reverse.dataset.tl = 'menu-reverse';
  reverse.addEventListener('click', run(options.onReverse));

  const scale = button('Scale ▸', 'tl-menu-item');
  scale.dataset.tl = 'menu-scale';

  const scaleSub = document.createElement('div');
  scaleSub.className = 'tl-menu-sub';
  scaleSub.hidden = true;
  for (const factor of SCALE_PRESETS) {
    const item = button(`${factor}×`, 'tl-menu-item');
    item.dataset.tlScale = String(factor);
    item.addEventListener('click', run(() => options.onScale(factor)));
    scaleSub.appendChild(item);
  }

  const scaleCustom = document.createElement('input');
  scaleCustom.type = 'number';
  scaleCustom.className = 'tl-num';
  scaleCustom.dataset.tl = 'menu-scale-custom';
  scaleCustom.step = '0.1';
  scaleCustom.min = String(SCALE_FACTOR_MIN);
  scaleCustom.max = String(SCALE_FACTOR_MAX);
  scaleCustom.title = 'Custom time scale (Enter to apply)';
  // Enter fires `keydown` AND, in a real browser, `change`; the menu is one-shot either way.
  let scaleFired = false;
  const fireScale = (): void => {
    if (scaleFired) return;
    const value = Number(scaleCustom.value);
    if (!Number.isFinite(value) || value <= 0) return;
    scaleFired = true;
    run(() => options.onScale(value))();
  };
  scaleCustom.addEventListener('change', fireScale);
  scaleCustom.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    fireScale();
  });
  scaleSub.appendChild(scaleCustom);

  scale.addEventListener('click', () => {
    scaleSub.hidden = !scaleSub.hidden;
  });

  const easing = button('Easing ▸', 'tl-menu-item');
  easing.dataset.tl = 'menu-easing';

  const submenu = document.createElement('div');
  submenu.className = 'tl-menu-sub';
  submenu.hidden = true;
  for (const preset of TWEEN_PRESETS) {
    const item = button(preset, 'tl-menu-item');
    item.dataset.tlEasing = preset;
    item.addEventListener('click', run(() => options.onEasing(preset)));
    submenu.appendChild(item);
  }
  easing.addEventListener('click', () => {
    submenu.hidden = !submenu.hidden;
  });

  menu.append(del, copy, reverse, scale, scaleSub, easing, submenu);
  options.host.appendChild(menu);

  document.addEventListener('pointerdown', onOutside, true);
  document.addEventListener('keydown', onKeyDown, true);

  return { element: menu, close };
}
